/**
 * Prepara un archivo para el escaner (facturas e inventario): lo convierte a
 * JPEG, lo reduce a un lado maximo y lo recomprime hasta que quepa.
 *
 * Mejora 44 de Alex (5 sep 2026): las fotos del iPhone pasadas al ordenador
 * llegan en HEIC, que Chrome y Firefox no saben abrir. Se convierten aqui
 * con heic2any (se carga solo cuando hace falta: pesa) y, si ni asi se
 * puede leer, el mensaje dice que formato subir en vez de un error opaco.
 */

/** Texto para ensenyar junto al boton de subida. */
export const FORMATOS_ESCANER = 'Formatos: JPG, PNG, HEIC (iPhone) o PDF.';

/** Lo que acepta el <input type="file">. */
export const ACCEPT_ESCANER =
  'image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif,application/pdf,image/*';

const MAX_HEIC_BYTES = 25_000_000;

export const ERROR_FORMATO =
  'Este formato no está soportado. Sube la imagen como JPG, PNG o PDF.';

function esHeic(file: File): boolean {
  const tipo = file.type.toLowerCase();
  if (tipo === 'image/heic' || tipo === 'image/heif') return true;
  // Muchos sistemas no ponen tipo al HEIC: se mira la extension.
  return /\.hei[cf]$/i.test(file.name);
}

async function heicAJpeg(file: File): Promise<Blob> {
  const { default: heic2any } = await import('heic2any');
  const salida = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
  return Array.isArray(salida) ? salida[0] : salida;
}

export interface OpcionesImagen {
  /** Lado mayor tras reducir. Mas para manuscrito (inventario) que para tiques. */
  maxLado: number;
  /** Peso maximo del PDF, que no se puede recomprimir aqui. */
  maxPdfBytes: number;
  /** Mensaje si el PDF pesa demasiado. */
  errorPdf: string;
  /** Mensaje si la imagen no cabe ni recomprimida. */
  errorTamanyo: string;
}

export async function prepararImagen(file: File, opts: OpcionesImagen): Promise<Blob> {
  if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) {
    if (file.size > opts.maxPdfBytes) throw new Error(opts.errorPdf);
    return file;
  }

  // Un HEIC se decodifica entero en memoria antes de reducirlo: con un tope
  // razonable no se cuelga la pestanya con una foto descomunal.
  if (esHeic(file) && file.size > MAX_HEIC_BYTES) throw new Error(opts.errorTamanyo);
  const fuente: Blob = esHeic(file) ? await heicAJpeg(file).catch(() => file) : file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(fuente);
  } catch {
    throw new Error(ERROR_FORMATO);
  }

  const escala = Math.min(1, opts.maxLado / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * escala);
  canvas.height = Math.round(bitmap.height * escala);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No se ha podido procesar la imagen.');
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  for (const quality of [0.92, 0.8, 0.65]) {
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', quality));
    if (blob && blob.size <= 4_000_000) return blob;
  }
  throw new Error(opts.errorTamanyo);
}
